import { ref } from 'vue'

import { manualInstallFor } from './installGuide'

// ==========================================================================
// PWA: installation and updates.
//
// Two independent signals tell us a new version is live, because neither is
// reliable on its own:
//
//   1. The service worker finds a new build and parks it in `waiting`.
//   2. A poll of /version.json finds a build id different from the running one.
//
// (2) matters because a browser may have the service worker disabled, and
// because it also catches the case where the API was redeployed while the tab
// stayed open for a week — which in an ERP is the normal case, not the edge one.
// ==========================================================================

const POLL_MS = 5 * 60_000

export const updateAvailable = ref(false)
export const installAvailable = ref(false)
export const isInstalled = ref(false)
// Set on Safari (iPhone, iPad, Mac): the app installs from a menu, not a
// dialog, so the button opens a guide instead. See installGuide.js.
export const manualInstall = ref(null)
export const installGuideOpen = ref(false)

let waitingWorker = null
let installPrompt = null
let pollTimer = null
let reloading = false
// True only when the user pressed "update". Reloading on any controller change
// would also reload on the very first install, when clients.claim() takes over
// a tab that never had a worker - a surprise reload on a first visit.
let updateAccepted = false
// Whether a worker was already controlling this tab when it loaded. If it was
// not, an "installed" worker is a first install, not a new version - even if
// another tab installed it a second earlier and claimed us in between.
let hadControllerAtStart = false

// Injected at build time by vite.config.js.
const RUNNING_BUILD = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'

// ── Service worker ──────────────────────────────────────────────────────────

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null

  // Never in development. The worker serves static assets cache-first, which is
  // correct in production (every filename carries a content hash) and actively
  // harmful in dev, where module URLs are stable and would be frozen at their
  // first version. Test the worker with `npm run build && npm run preview`.
  if (import.meta.env.DEV) {
    // Clean up a worker left behind by an earlier session, or the developer
    // keeps getting stale code with no idea why.
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map(registration => registration.unregister()))
    return null
  }

  try {
    // Con el prefijo delante, los dos. Un `/sw.js` absoluto en una instancia
    // servida bajo subruta pide un fichero que ahi no esta, y el registro
    // falla en silencio: lo que se pierde no es la demo, es el aviso de que
    // hay version nueva.
    const base = import.meta.env.BASE_URL
    const registration = await navigator.serviceWorker.register(`${base}sw.js`, { scope: base })

    // A new worker was already waiting when this tab opened.
    if (registration.waiting) {
      waitingWorker = registration.waiting
      updateAvailable.value = true
    }

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing
      if (!installing) return

      installing.addEventListener('statechange', () => {
        if (installing.state !== 'installed') return
        // Only an update if this tab was already controlled when it loaded;
        // otherwise it is a first install and there is nothing to announce.
        if (!hadControllerAtStart) return

        waitingWorker = installing
        updateAvailable.value = true
      })
    })

    // Reload only after the user accepted: see updateAccepted above.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!updateAccepted || reloading) return
      reloading = true
      window.location.reload()
    })

    return registration
  } catch (error) {
    // A failed service worker must never stop the app from running.
    console.warn('[pwa] service worker registration failed', error)
    return null
  }
}

// ── Version polling ─────────────────────────────────────────────────────────

async function checkVersion(registration) {
  try {
    // cache: no-store, or the browser happily hands back the old file forever.
    const response = await fetch(`${import.meta.env.BASE_URL}version.json`, { cache: 'no-store' })
    if (!response.ok) return

    // In development the dev server answers unknown paths with index.html and a
    // 200, so the status alone is not enough to trust the body.
    if (!response.headers.get('content-type')?.includes('application/json')) return

    const { build } = await response.json()
    if (build && build !== RUNNING_BUILD) updateAvailable.value = true
  } catch {
    // Offline, or a body that is not the JSON we expect: check again later.
  }

  // Ask the browser to re-fetch sw.js as well, which is what produces signal (1).
  registration?.update().catch(() => {})
}

// ── Public API ──────────────────────────────────────────────────────────────

/** Applies the pending update. Reloads the tab. */
export function applyUpdate() {
  updateAccepted = true

  if (waitingWorker) {
    // The worker calls skipWaiting and then takes control, which fires
    // controllerchange above and reloads us.
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
    return
  }
  // No service worker: a plain reload picks up the new index.html.
  reloading = true
  window.location.reload()
}

export function dismissUpdate() {
  updateAvailable.value = false
}

/** Shows the browser's native install dialog. */
export async function promptInstall() {
  if (!installPrompt) return false

  installPrompt.prompt()
  const { outcome } = await installPrompt.userChoice

  // The event can only be used once; the browser will fire a new one if the
  // user declines and becomes eligible again.
  installPrompt = null
  installAvailable.value = false

  return outcome === 'accepted'
}

export function setupPwa() {
  // Captured before registering: after register() another tab may have claimed
  // this one, and then we could no longer tell an update from a first install.
  hadControllerAtStart = Boolean(navigator.serviceWorker?.controller)

  // Already running as an installed app: no point offering to install it.
  isInstalled.value = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true

  manualInstall.value = manualInstallFor({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    standalone: isInstalled.value,
  })

  window.addEventListener('beforeinstallprompt', (event) => {
    // Prevent the default mini-infobar so the offer appears where we decide,
    // in the top bar, rather than covering the app.
    event.preventDefault()
    installPrompt = event
    installAvailable.value = true
  })

  window.addEventListener('appinstalled', () => {
    isInstalled.value = true
    installAvailable.value = false
    manualInstall.value = null
    installPrompt = null
  })

  registerServiceWorker().then(registration => {
    checkVersion(registration)

    clearInterval(pollTimer)
    pollTimer = setInterval(() => checkVersion(registration), POLL_MS)

    // Coming back to the tab is the moment a stale version is most likely and
    // most disruptive to discover the hard way.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkVersion(registration)
    })
  })
}
